/**
 * The value contract between a date filter host and the Livewire property it is bound to
 * (blink-superset-bundle.md, "The x-filters hosts").
 *
 * The property holds a Superset time_range in the forms a report stores (layout-grid.md): a preset
 * key, `all_time`, `No filter`, or a literal `YYYY-MM-DD : YYYY-MM-DD` with an exclusive end; the
 * date filter holds one day as `D : D+1`, or null. The picker takes only a literal or nothing, so a
 * preset key is shown with the dates PHP resolved for it — never the component's own, which are the
 * browser's, with Sunday weeks.
 */
export const NO_FILTER = 'No filter';
export const ALL_TIME = 'all_time';

const LITERAL = /^\d{4}-\d{2}-\d{2} : \d{4}-\d{2}-\d{2}$/;

export function isLiteral(value) {
    return typeof value === 'string' && LITERAL.test(value);
}

/**
 * What the picker is mounted with for a bound value: `value` for its filterState, `presetKey` for
 * the preset it highlights. `readable` is false for a value no rule here reads, which is shown as an
 * empty picker rather than refused.
 */
export function toPickerState(value, { kind, presetRanges = {} }) {
    if (isLiteral(value)) return { value, presetKey: null, readable: true };

    if (kind === 'dateRange' && value === ALL_TIME) {
        return { value: undefined, presetKey: ALL_TIME, readable: true };
    }

    if (kind === 'dateRange' && typeof value === 'string' && isLiteral(presetRanges[value])) {
        return { value: presetRanges[value], presetKey: value, readable: true };
    }

    const empty = value === null || value === undefined || value === '' || value === NO_FILTER;

    return { value: undefined, presetKey: null, readable: empty };
}

/**
 * What a DataMask from the picker writes to the bound property, if anything. The component's own
 * midnight rollover of a preset picked in the session (`auto`) carries the browser's recomputation
 * of that preset, so it writes nothing. `shown` is the literal the component now displays.
 */
export function fromDataMask(dataMask, { presetKey = null, auto = false } = {}, { kind }) {
    if (auto) return { write: false };

    const literal = isLiteral(dataMask?.filterState?.value) ? dataMask.filterState.value : undefined;

    if (kind === 'date') return { write: true, value: literal ?? null, shown: literal };

    // A clear and All Time emit the same empty mask; only the preset key tells them apart.
    if (presetKey === ALL_TIME) return { write: true, value: ALL_TIME, shown: undefined };
    if (literal === undefined) return { write: true, value: NO_FILTER, shown: undefined };

    return { write: true, value: presetKey ?? literal, shown: literal };
}
